import { useState, useEffect, useRef } from 'react';
import { FaPhoneAlt, FaMicrophone, FaMicrophoneSlash } from 'react-icons/fa';
import axios from "axios";

const BusinessCallPanel = ({ isVisible, setIsVisible, callerName, callerMobileNumber }) => {
    const ApiBaseURL = process.env.REACT_APP_GROUP_URL;
    const token = localStorage.getItem("authToken");

    const [callActive, setCallActive] = useState(false);
    const [isRinging, setIsRinging] = useState(false);
    const [callDuration, setCallDuration] = useState(0);
    const [micMuted, setMicMuted] = useState(false);
    const [pc, setPc] = useState(null);

    const [callingMessageId, setCallingMessageId] = useState("");

    const localAudioRef = useRef();
    const remoteAudioRef = useRef();
    const ringtoneRef = useRef(null);
    const localStreamRef = useRef(null);

    // Inside your CallPanel component
    const [mediaRecorder, setMediaRecorder] = useState(null);
    const recordedChunksRef = useRef([]);

    const isVoiceRecordingEnabled = localStorage.getItem("isVoiceRecordingEnabled") === 'true';

    // Call timer
    useEffect(() => {
        let timer;
        if (callActive) timer = setInterval(() => setCallDuration(t => t + 1), 1000);
        return () => clearInterval(timer);
    }, [callActive]);

    const formatTime = (s) => {
        const m = String(Math.floor(s / 60)).padStart(2, "0");
        const sec = String(s % 60).padStart(2, "0");
        return `${m}:${sec}`;
    };

    // 🔔 Auto start call when modal visible
    useEffect(() => {
        if (isVisible && !callActive) {
            setIsRinging(true);
            startOutgoingCall();
        }

        return () => stopRingtone();
    }, [isVisible]);

    // 🔊 Play ringtone
    const playRingtone = () => {
        ringtoneRef.current = new Audio("https://wabareactapi.linkext.co.in/NotificationSound/PhoneCalling.mp3");
        ringtoneRef.current.loop = true;
        ringtoneRef.current.play().catch(() => console.warn("Autoplay blocked"));
    };

    const stopRingtone = () => {
        if (ringtoneRef.current) {
            ringtoneRef.current.pause();
            ringtoneRef.current.currentTime = 0;
            ringtoneRef.current = null;
        }
    };

    const startRecording = async (localStream, remoteStream = null) => {
        if (!isVoiceRecordingEnabled) return;

        try {
            const audioContext = new AudioContext();
            const destination = audioContext.createMediaStreamDestination();

            // Create source nodes for both streams
            const localSource = audioContext.createMediaStreamSource(localStream);
            localSource.connect(destination);

            if (remoteStream) {
                const remoteSource = audioContext.createMediaStreamSource(remoteStream);
                remoteSource.connect(destination);
            }

            // ✅ Mixed stream (mono output)
            const mixedStream = destination.stream;

            const recorder = new MediaRecorder(mixedStream);
            recordedChunksRef.current = [];

            recorder.ondataavailable = (event) => {
                if (event.data.size > 0) recordedChunksRef.current.push(event.data);
            };

            recorder.onstop = () => {
                const blob = new Blob(recordedChunksRef.current, { type: "audio/webm" });

                // Generate filename
                const currentDate = new Date();
                const day = String(currentDate.getDate()).padStart(2, '0');
                const month = String(currentDate.getMonth() + 1).padStart(2, '0');
                const year = String(currentDate.getFullYear()).slice(2);
                const hours = String(currentDate.getHours()).padStart(2, '0');
                const minutes = String(currentDate.getMinutes()).padStart(2, '0');
                const seconds = String(currentDate.getSeconds()).padStart(2, '0');
                const timestamp = `${day}${month}${year}${hours}${minutes}${seconds}`;
                const filename = `${callerMobileNumber}_${timestamp}.webm`;

                // Download automatically
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = filename;
                a.click();
                URL.revokeObjectURL(url);
            };

            recorder.start();
            setMediaRecorder(recorder);

            console.log("🎙️ Recording started (MIXED local + remote audio)");
        } catch (err) {
            console.error("❌ Recording error:", err);
        }
    };

    // === Start Outgoing Call ===
    const startOutgoingCall = async () => {
        try {
            playRingtone();
            console.log("🎧 Creating WebRTC offer...");

            const peerConnection = new RTCPeerConnection({
                iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
            });
            setPc(peerConnection);

            // Local audio
            const localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
            localStreamRef.current = localStream;
            localStream.getTracks().forEach(track => peerConnection.addTrack(track, localStream));
            localAudioRef.current.srcObject = localStream;

            // Remote audio
            const remoteStream = new MediaStream();
            peerConnection.ontrack = (e) => {
                e.streams[0].getTracks().forEach((track) => remoteStream.addTrack(track));
                remoteAudioRef.current.srcObject = remoteStream;
            };

            // Create local offer SDP
            const offer = await peerConnection.createOffer();
            await peerConnection.setLocalDescription(offer);
            const localSdp = offer.sdp;

            console.log("📤 Sending CallDialing API with localSdp...");

            const payload = {
                customerMobileNumber: callerMobileNumber,
                localSdp: localSdp,
            };

            const response = await axios.post(`${ApiBaseURL}Calling/CallDialing`, payload, {
                headers: {
                    "Content-Type": "application/json",
                }
            });

            // ✅ Check for error in response
            if (response.data?.error) {
                const errorMsg = response.data.error.message || "Call failed.";

                // 🛑 Reset everything on error
                stopRingtone();
                stopRecording();
                stopLocalMic();
                cleanupPeerConnection();

                setCallActive(false);
                setIsRinging(false);
                setCallDuration(0);
                setIsVisible(false);

                alert(errorMsg);
                return;
            }

            if (response.data?.calls?.length > 0) {
                const messageId = response.data.calls[0].id;
                setCallingMessageId(messageId);
                console.log("📞 Call Message ID set:", messageId);
                pollUntilConnected(messageId, peerConnection, localSdp);
            } else {
                console.warn("⚠️ No call ID found in response", response.data);
            }
        } catch (error) {
            console.error("Dialing error:", error);
        }
    };

    // Polling for call status
    const pollUntilConnected = async (messageId, peerConnection, localSdp) => {
        console.log("🔁 Checking call status...");

        const interval = setInterval(async () => {
            try {
                const res = await axios.get(`${ApiBaseURL}Calling/CallStatus?MessageId=${messageId}`);

                // ✅ res.data is an array
                if (res.data && res.data.length > 0 && res.data[0].Call_Event === "connect") {
                    console.log("✅ Call Connected!");
                    clearInterval(interval);

                    // 🟢 Extract the SDP from API response
                    const remoteSdp = res.data[0].Call_Session_sdp;

                    // Set remote SDP
                    try {
                        await peerConnection.setRemoteDescription({ type: "answer", sdp: remoteSdp });
                        console.log("✅ Remote SDP applied, audio should flow now");
                    } catch (err) {
                        console.error("❌ Error applying remote SDP:", err);
                    }

                    // CallAccepted API
                    const payload = {
                        customerMobileNumber: callerMobileNumber,
                        messageId: callingMessageId,
                        localSdp: localSdp,
                        initiatedType: "business",
                    };

                    try {
                        const response = await fetch(
                            `${ApiBaseURL}Calling/CallAccepted`,
                            {
                                method: "POST",
                                headers: {
                                    "Content-Type": "application/json",
                                },
                                body: JSON.stringify(payload),
                            }
                        );

                        stopRingtone();
                        setCallActive(true);
                        setIsRinging(false);

                        // Start recording after remote SDP applied if enabled
                        if (isVoiceRecordingEnabled) {
                            const remoteStream = new MediaStream();
                            peerConnection.getReceivers().forEach(receiver => {
                                if (receiver.track) remoteStream.addTrack(receiver.track);
                            });
                            startRecording(localStreamRef.current, remoteStream);
                        }

                        // Start triminate check every 1 sec
                        const checkCallEnd = setInterval(async () => {
                            try {
                                const res = await axios.get(`${ApiBaseURL}Calling/CallStatus?MessageId=${messageId}`);

                                if (res.data && res.data.length > 0 && res.data[0].IsTerminate === true) {
                                    console.log("📞 Call Ended by remote!");
                                    clearInterval(checkCallEnd);
                                    handleEndCall();
                                }
                            } catch (err) {
                                console.error("❌ Error checking call triminate:", err);
                            }
                        }, 1000);

                        console.log("Call accepted:");
                    } catch (error) {
                        console.error(error);
                    }
                }

            } catch (err) {
                console.error("❌ Error checking call status:", err);
            }
        }, 3000);
    };

    const handleEndCall = async () => {
        try {

            // 🛑 Stop and save recording
            if (mediaRecorder && mediaRecorder.state !== "inactive") {
                mediaRecorder.stop();
            }

            // 🎤 Stop microphone (local audio)
            if (localStreamRef.current) {
                localStreamRef.current.getTracks().forEach(track => track.stop());
                localStreamRef.current = null;
            }

            // 🛑 Close PeerConnection
            if (pc) {
                pc.close();
                setPc(null);
            }
            stopRingtone();
            setCallActive(false);
            setIsRinging(false);
            setCallDuration(0);
            setIsVisible(false);

            // ✅ Call Terminate API
            const response = await fetch(
                `${ApiBaseURL}Calling/CallTerminate?MessageId=${callingMessageId}`
            );

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const result = await response.json();
            console.log("Call terminated successfully:", result);
        } catch (error) {
            console.error("Error terminating call:", error);
        }
    };

    const handleToggleMic = () => {
        if (localStreamRef.current) {
            const audioTrack = localStreamRef.current.getAudioTracks()[0];
            if (audioTrack) {
                audioTrack.enabled = !audioTrack.enabled;
                setMicMuted(!audioTrack.enabled);
            }
        }
    };

    // 🧹 Stop recording safely
    const stopRecording = () => {
        if (mediaRecorder && mediaRecorder.state !== "inactive") {
            mediaRecorder.stop();
            setMediaRecorder(null);
        }
    };

    // 🎤 Stop local mic stream
    const stopLocalMic = () => {
        if (localStreamRef.current) {
            localStreamRef.current.getTracks().forEach(track => track.stop());
            localStreamRef.current = null;
        }
    };

    // 🔌 Close peer connection
    const cleanupPeerConnection = () => {
        if (pc) {
            pc.close();
            setPc(null);
        }
    };

    return (
        <div className={`fixed inset-0 z-50 flex justify-center items-center ${isVisible ? "" : "hidden"}`}>
            <div className="bg-black shadow-xl rounded-xl p-6 w-72 text-center border border-gray-200">
                <div className="flex flex-col items-center mb-4">
                    <div className="w-24 h-24 bg-gray-300 rounded-full flex justify-center items-center mb-2">
                        <span className="text-white text-3xl">{callerName?.[0]}</span>
                    </div>
                    <p className="text-xl font-bold text-white">{callerName}</p>
                </div>

                <div className="mb-2 text-white">
                    <p className="text-lg">Phone: {callerMobileNumber}</p>
                    {isRinging && <p className="text-yellow-300 text-lg font-semibold mt-1">📞 Ringing...</p>}
                    {callActive && <p className="text-white text-sm mt-1">Duration: {formatTime(callDuration)}</p>}
                </div>

                <div className="flex justify-around items-center mt-4">
                    {callActive && (
                        <button
                            className="flex items-center justify-center w-12 h-12 bg-gray-600 text-white rounded-full hover:bg-gray-700"
                            onClick={handleToggleMic}
                            title={micMuted ? "Unmute Mic" : "Mute Mic"}
                        >
                            {micMuted ? <FaMicrophoneSlash /> : <FaMicrophone />}
                        </button>
                    )}

                    <button
                        className="flex items-center justify-center w-12 h-12 bg-red-500 text-white rounded-full hover:bg-red-600"
                        onClick={handleEndCall}
                        title="End Call"
                    >
                        <FaPhoneAlt />
                    </button>
                </div>

                <audio ref={localAudioRef} autoPlay muted></audio>
                <audio ref={remoteAudioRef} autoPlay></audio>
            </div>
        </div>
    );
};

export default BusinessCallPanel;
